import { useAppTheme } from "@hooks/useAppTheme";
import type { MixedItemTag } from "@memorang/types";
import { Box, useDevice } from "@memorang/ui";
import { ModuleActionButton } from "components/ModuleActionButton";
import { ModuleMetadataChips } from "components/ModuleMetadataChips";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Card, Text } from "react-native-paper";
import { buttonConfig } from "../constants";
import { getProethicaModuleInteractionState } from "../helpers/proethica-module-list";
import type { ProethicaScoreRowData } from "../helpers/proethica-progress-data";
import { KnowledgeCheckProgress } from "./KnowledgeCheckProgress";
import { ModuleListItem, type ModuleListItemProps } from "./ModuleListItem";
import { ModuleThumbnail } from "./ModuleThumbnail";
import { ProethicaTestCompletedBadge } from "./ProethicaTestCompletedBadge";
import { ModulesListSkeleton } from "./Skeletons/ModulesListSkeleton";

type ModuleListItemMapper = (
	tag: MixedItemTag,
	t: TFunction,
) => ModuleListItemProps & {
	id: string;
};

type Props = {
	items: MixedItemTag[];
	scoreRows?: ProethicaScoreRowData[];
	loading?: boolean;
	handleSelectTopic: (id: string) => void;
	handleShowPurchase: () => void;
	getModuleListItemProps: ModuleListItemMapper;
	viewMode?: "list" | "grid";
};

type ModuleGridItemProps = ModuleListItemProps & {
	onPress: () => void;
};

type MappedModuleItem = {
	id: string;
	moduleProps: ModuleListItemProps & { id: string };
	onPress: () => void;
};

const isMappedModuleItem = (
	item: MappedModuleItem | null,
): item is MappedModuleItem => item !== null;

const chunkModuleRows = (items: MappedModuleItem[], columns: number) => {
	const rows: MappedModuleItem[][] = [];
	for (let index = 0; index < items.length; index += columns) {
		rows.push(items.slice(index, index + columns));
	}
	return rows;
};

const MODULE_GRID_CARD_HEIGHT_WITH_SCORE = 188;
const MODULE_GRID_TEXT_BLOCK_HEIGHT_WITH_SCORE = 96;
const MODULE_GRID_CARD_HEIGHT = 168;
const MODULE_GRID_TEXT_BLOCK_HEIGHT = 76;

const getModuleGridHeights = (
	showKnowledgeCheckProgress: boolean,
	isMobile: boolean,
) => {
	if (isMobile) {
		return { cardHeight: undefined, textBlockHeight: undefined };
	}

	return {
		cardHeight: showKnowledgeCheckProgress
			? MODULE_GRID_CARD_HEIGHT_WITH_SCORE
			: MODULE_GRID_CARD_HEIGHT,
		textBlockHeight: showKnowledgeCheckProgress
			? MODULE_GRID_TEXT_BLOCK_HEIGHT_WITH_SCORE
			: MODULE_GRID_TEXT_BLOCK_HEIGHT,
	};
};

const ModuleGridItem = ({
	title,
	subtitle,
	status,
	cta,
	thumbnailUrl,
	thumbnailSource,
	hasAccess = true,
	disabled,
	onPress,
	loading,
	duration,
	durationLabel,
	typeLabel,
	isTest,
	testKind,
	icon,
	scorePercent,
	showKnowledgeCheckProgress = false,
}: ModuleGridItemProps & { showKnowledgeCheckProgress?: boolean }) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { cardHeight, textBlockHeight } = getModuleGridHeights(
		showKnowledgeCheckProgress,
		isMobile,
	);
	const { buttonMode, buttonIcon } = buttonConfig[status];
	const finalDurationLabel =
		duration !== undefined ? (durationLabel ?? `${duration} min`) : undefined;
	const metadataChipProps = isTest
		? { typeLabel }
		: { durationLabel: finalDurationLabel };
	const { needsPurchase, isCurriculumLocked } =
		getProethicaModuleInteractionState(
			hasAccess,
			disabled,
			status === "COMPLETE",
		);
	const showScore = status === "COMPLETE" && scorePercent != null;
	const showTestCompletedBadge = showScore && isTest;

	return (
		<Card
			mode="outlined"
			onPress={isCurriculumLocked ? undefined : onPress}
			contentStyle={cardHeight != null ? { height: cardHeight } : undefined}
			style={{
				borderRadius: theme.roundness,
				backgroundColor: theme.colors.surface,
				borderColor: theme.dark
					? theme.colors.outline
					: theme.colors.outlineVariant,
				overflow: "hidden",
				...(cardHeight != null ? { height: cardHeight } : {}),
			}}
		>
			<Box
				padding={16}
				flex={1}
				justifyContent="space-between"
				style={{
					gap: 12,
					...(cardHeight != null ? { height: cardHeight } : {}),
				}}
			>
				<Box flexDirection="row" gap={12} alignItems="flex-start" flex={1}>
					<ModuleThumbnail
						size={64}
						title={title}
						thumbnailSource={thumbnailSource}
						thumbnailUrl={thumbnailUrl}
						locked={isCurriculumLocked}
						isTest={isTest}
						testKind={testKind}
						icon={icon}
					/>
					<Box flex={1} gap={12}>
						<Box
							gap={4}
							style={{
								...(textBlockHeight != null
									? { maxHeight: textBlockHeight, overflow: "hidden" }
									: {}),
							}}
						>
							<Text
								variant="titleSmall"
								numberOfLines={2}
								ellipsizeMode="tail"
								style={{
									fontWeight: "700",
									color: theme.colors.onSurface,
								}}
							>
								{title}
							</Text>
							{showTestCompletedBadge ? null : (
								<Text
									variant="bodySmall"
									numberOfLines={2}
									ellipsizeMode="tail"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{subtitle}
								</Text>
							)}
						</Box>
						{showTestCompletedBadge ? (
							<ProethicaTestCompletedBadge scorePercent={scorePercent} />
						) : showScore ? (
							<KnowledgeCheckProgress scorePercent={scorePercent} />
						) : null}
					</Box>
				</Box>
				<Box
					flexDirection="row"
					alignItems="flex-end"
					justifyContent="space-between"
					gap={12}
				>
					<ModuleMetadataChips
						{...metadataChipProps}
						style={{ flexShrink: 1 }}
					/>
					<ModuleActionButton
						mode={needsPurchase ? "contained-tonal" : buttonMode}
						icon={needsPurchase ? "lock-outline" : buttonIcon}
						cta={cta}
						loading={loading}
						disabled={needsPurchase ? false : loading || isCurriculumLocked}
						onPress={onPress}
					/>
				</Box>
			</Box>
		</Card>
	);
};

export const ModulesList = ({
	items,
	scoreRows,
	loading,
	handleSelectTopic,
	handleShowPurchase,
	getModuleListItemProps,
	viewMode = "list",
}: Props) => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const showKnowledgeCheckProgress = scoreRows != null;

	if (loading) {
		return <ModulesListSkeleton viewMode={viewMode} />;
	}

	const scorePercentById = showKnowledgeCheckProgress
		? new Map(scoreRows.map((row) => [row.id, row.scorePercent]))
		: undefined;

	const mappedItems = items
		.map((tag) => {
			if (!("distribution" in tag)) {
				return null;
			}
			const { id } = tag;
			const moduleProps = getModuleListItemProps(tag, t);
			const scorePercent = scorePercentById?.get(String(id));
			const onPress = () => {
				if (!moduleProps.hasAccess) {
					handleShowPurchase();
					return;
				}
				if (moduleProps.disabled) {
					return;
				}
				handleSelectTopic(String(id));
			};
			return {
				id,
				moduleProps: {
					...moduleProps,
					...(scorePercent != null ? { scorePercent } : {}),
				},
				onPress,
			};
		})
		.filter(isMappedModuleItem);

	if (viewMode === "grid") {
		const columnCount = isMobile ? 1 : 2;
		const rows = chunkModuleRows(mappedItems, columnCount);

		return (
			<Box style={{ gap: 16 }}>
				{rows.map((row) => (
					<Box
						key={row.map((item) => item.id).join("-")}
						flexDirection={isMobile ? "column" : "row"}
						style={{ gap: 16 }}
					>
						{row.map((item) => (
							<Box key={item.id} flex={1} minWidth={0}>
								<ModuleGridItem
									{...item.moduleProps}
									onPress={item.onPress}
									showKnowledgeCheckProgress={showKnowledgeCheckProgress}
								/>
							</Box>
						))}
						{row.length < columnCount
							? Array.from({ length: columnCount - row.length }).map(
									(_, index) => (
										<Box
											key={`grid-spacer-${row[0]?.id}-${index}`}
											flex={1}
											minWidth={0}
										/>
									),
								)
							: null}
					</Box>
				))}
			</Box>
		);
	}

	return (
		<Box style={{ gap: 12 }}>
			{mappedItems.map((item) => (
				<ModuleListItem
					key={item.id}
					{...item.moduleProps}
					onPress={item.onPress}
				/>
			))}
		</Box>
	);
};
